/**
 * Subscription Success Page
 * Shown after successful Stripe checkout completion
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    const verifyCheckout = async () => {
      if (!sessionId) {
        setStatus('success'); // No session ID means direct navigation, assume success
        return;
      }
      
      try {
        // Get auth token
        const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
        if (!token) {
          setStatus('success'); // No token, show success anyway
          return;
        }
        
        // Verify checkout status
        const response = await fetch(`${API_URL}/api/stripe/checkout-status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid' || data.status === 'complete') {
            setStatus('success');
            setMessage('Your premium subscription is now active!');
          } else {
            setStatus('success');
            setMessage('Your subscription is being processed.');
          }
        } else {
          setStatus('success');
          setMessage('Welcome to Premium!');
        }
      } catch (error) {
        console.error('Error verifying checkout:', error);
        setStatus('success');
        setMessage('Welcome to Premium!');
      }
    };
    
    verifyCheckout();
  }, [sessionId]);
  
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Verifying your subscription...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Success Card */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 text-center border border-gray-700 shadow-2xl">
          {/* Animated Success Icon */}
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
            <div className="absolute inset-2 bg-emerald-500/30 rounded-full animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <CheckCircle className="w-16 h-16 text-emerald-500" />
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome to Premium!
          </h1>
          
          {/* Subtitle */}
          <p className="text-gray-400 mb-6">
            {message || 'Your subscription is now active. Enjoy all premium features!'}
          </p>
          
          {/* Premium Features */}
          <div className="bg-gray-800/50 rounded-2xl p-4 mb-6 text-left">
            <h3 className="text-amber-400 text-sm font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Now Unlocked
            </h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Identity & Beliefs Modules
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Goals & Action Plans
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Advanced Forecasting
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Personal Finance Journal
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                Wealth Ladder Education
              </li>
            </ul>
          </div>
          
          {/* CTA Button */}
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 rounded-xl text-lg shadow-lg shadow-amber-500/20"
            data-testid="success-go-to-dashboard"
          >
            Go to Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Questions? Contact support@prospermind.com
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
